import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/Button.tsx';
import { initAuthFromCallback } from '@/lib/auth.ts';
import { useAuthStore } from '@/stores/auth.ts';

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const { user, isInitialized } = useAuthStore();

  useEffect(() => {
    // Check for error in query params
    const errorMessage = searchParams.get('message');
    if (errorMessage) {
      setError(decodeURIComponent(errorMessage));
      return;
    }

    // Try to initialize auth from URL (async)
    void (async () => {
      try {
        const success = await initAuthFromCallback();
        if (!success && !searchParams.get('exchangeToken')) {
          setError('Invalid callback - no authentication data received');
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        setError('Authentication failed. Please try again.');
      }
    })();
  }, [searchParams]);

  // Redirect after auth is initialized
  useEffect(() => {
    if (isInitialized && user) {
      // If user has no tenants, redirect to onboarding
      if (user.tenants.length === 0) {
        navigate('/onboarding', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [isInitialized, user, navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm text-center">
          <div className="mb-4 flex justify-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error/10">
              <AlertCircle className="h-6 w-6 text-error" />
            </div>
          </div>
          <h1 className="mb-2 text-xl font-semibold text-foreground">
            Authentication failed
          </h1>
          <p className="mb-6 text-sm text-foreground-muted">{error}</p>
          <Button variant="primary" onClick={() => navigate('/login')}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm text-center">
        <div className="mb-4 flex justify-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary">
            <Box className="h-7 w-7 text-primary-foreground" />
          </div>
        </div>
        <div className="mb-2 flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <span className="text-foreground">Signing you in...</span>
        </div>
        <p className="text-sm text-foreground-muted">
          Please wait while we complete your authentication
        </p>
      </div>
    </div>
  );
}
